import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Trophy, Truck, DollarSign, Target, RefreshCw, Loader2,
  TrendingUp, TrendingDown, AlertOctagon, Award, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import api from '../lib/api';

const GRADE_TONE = {
  'A+': 'bg-emerald-500 text-white',
  'A':  'bg-emerald-400 text-emerald-950',
  'B':  'bg-sky-400 text-sky-950',
  'C':  'bg-amber-400 text-amber-950',
  'D':  'bg-orange-500 text-white',
  'F':  'bg-rose-500 text-white',
};

const SCORE_TONE = (s) => {
  if (s >= 80) return 'text-emerald-700';
  if (s >= 60) return 'text-amber-700';
  return 'text-rose-700';
};

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function SupplierScorecardPage() {
  const [days, setDays] = useState(180);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/suppliers/scorecard', { params: { days } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { reload(); }, [reload]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const cols = ['Supplier', 'POs', 'Received', 'Open', 'Avg delivery days', 'Cost CV %', 'Accuracy %', 'Delivery score', 'Cost score', 'Accuracy score', 'Overall', 'Grade'];
    const lines = [cols.join(',')];
    data.rows.forEach(r => lines.push([
      csvEscape(r.name), r.po_count, r.received_po_count, r.open_po_count,
      r.avg_delivery_days ?? '—', r.cost_cv, r.accuracy_score,
      r.delivery_score, r.cost_score, r.accuracy_score, r.overall_score, r.grade,
    ].join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `supplier-scorecard-${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading scorecard…
    </div>
  );

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const top = rows[0];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5" data-testid="supplier-scorecard-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-7 w-7 text-amber-500" />
            Supplier Scorecard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Delivery time · cost consistency · order accuracy. Computed live from your purchase-order history.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="h-9 w-36" data-testid="window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
              <SelectItem value="730">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={reload} className="gap-1.5" data-testid="reload-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0} className="gap-1.5" data-testid="export-csv-btn">
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Suppliers tracked" value={summary.total_suppliers || 0} icon={Truck} tone="slate" />
        <Tile label="Avg overall" value={`${summary.avg_overall || 0}`} icon={TrendingUp} tone={summary.avg_overall >= 70 ? 'emerald' : summary.avg_overall >= 50 ? 'amber' : 'rose'} />
        <Tile label="Top performer" value={summary.top_performer || '—'} icon={Award} tone="emerald" subtle />
        <Tile label="Underperformers" value={(summary.underperformers || []).length} icon={AlertOctagon} tone={summary.underperformers?.length ? 'rose' : 'slate'} />
      </div>

      {/* Empty state */}
      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed py-16 text-center text-muted-foreground" data-testid="scorecard-empty">
          <Truck className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-base font-semibold">No purchase orders in this window</p>
          <p className="text-sm">Try widening the time range, or create some POs first.</p>
        </div>
      ) : (
        <>
          {/* Podium for top 3 */}
          {rows.length >= 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="podium">
              {[rows[1], rows[0], rows[2]].filter(Boolean).map((r, idx) => {
                const isFirst = r === top;
                return (
                  <div
                    key={r.supplier_id}
                    className={`rounded-2xl border p-5 ${
                      isFirst
                        ? 'border-amber-300 bg-gradient-to-b from-amber-50 to-amber-50/30 md:order-2 md:scale-105'
                        : 'border-border bg-card md:order-' + (idx === 0 ? '1' : '3')
                    }`}
                    data-testid={`podium-${r.supplier_id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {isFirst ? <Trophy className="h-5 w-5 text-amber-500" /> : <Award className="h-5 w-5 text-sky-500" />}
                        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                          #{rows.indexOf(r) + 1}
                        </span>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${GRADE_TONE[r.grade]}`}>
                        {r.grade}
                      </span>
                    </div>
                    <p className="text-lg font-bold truncate">{r.name}</p>
                    <p className={`text-3xl font-bold tabular-nums ${SCORE_TONE(r.overall_score)}`}>{r.overall_score}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-3">Overall score / 100</p>
                    <div className="grid grid-cols-3 gap-1 text-center">
                      <MiniMetric label="Delivery" value={r.delivery_score} />
                      <MiniMetric label="Cost" value={r.cost_score} />
                      <MiniMetric label="Accuracy" value={r.accuracy_score} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Detailed table */}
          <div className="rounded-xl border bg-card overflow-hidden" data-testid="scorecard-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground border-b bg-muted/30">
              <div className="col-span-3">Supplier</div>
              <div className="col-span-1 text-right">POs</div>
              <div className="col-span-2">Avg delivery</div>
              <div className="col-span-2">Cost CV</div>
              <div className="col-span-2">Accuracy</div>
              <div className="col-span-1 text-right">Score</div>
              <div className="col-span-1 text-right">Grade</div>
            </div>
            <div className="divide-y">
              {rows.map(r => (
                <div key={r.supplier_id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-muted/20 transition-colors" data-testid={`row-${r.supplier_id}`}>
                  <div className="col-span-3 min-w-0">
                    <p className="font-semibold truncate">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {r.tracked_products} products tracked · {r.line_items} lines
                    </p>
                  </div>
                  <div className="col-span-1 text-right tabular-nums">
                    <p className="font-semibold">{r.po_count}</p>
                    {r.open_po_count > 0 && <p className="text-[10px] text-amber-700">{r.open_po_count} open</p>}
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-sky-500 opacity-60" />
                    <div>
                      <p className="text-sm font-semibold tabular-nums">
                        {r.avg_delivery_days != null ? `${r.avg_delivery_days} d` : '—'}
                      </p>
                      <ScoreBar value={r.delivery_score} />
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-500 opacity-60" />
                    <div>
                      <p className="text-sm font-semibold tabular-nums">{r.cost_cv}%</p>
                      <ScoreBar value={r.cost_score} />
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <Target className="h-4 w-4 text-amber-500 opacity-60" />
                    <div>
                      <p className="text-sm font-semibold tabular-nums">
                        {r.total_received}/{r.total_ordered}
                      </p>
                      <ScoreBar value={r.accuracy_score} />
                    </div>
                  </div>
                  <div className={`col-span-1 text-right text-2xl font-bold tabular-nums ${SCORE_TONE(r.overall_score)}`}>
                    {r.overall_score}
                  </div>
                  <div className="col-span-1 text-right">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${GRADE_TONE[r.grade]}`}>{r.grade}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground text-center">
            Window: last {data.window_days} days · Updated just now ·
            Score = Delivery 40% · Cost consistency 30% · Order accuracy 30%
          </p>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone, subtle }) {
  const TONE = {
    slate:   'border-slate-200 bg-slate-50 text-slate-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber:   'border-amber-200 bg-amber-50 text-amber-800',
    rose:    'border-rose-200 bg-rose-50 text-rose-800',
  };
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${TONE[tone] || TONE.slate}`}>
      <Icon className="h-5 w-5 opacity-70 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
        <p className={`font-bold tabular-nums leading-tight truncate ${subtle ? 'text-base' : 'text-2xl'}`}>{value}</p>
      </div>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-md bg-white/70 dark:bg-black/20 border border-border/50 py-1">
      <p className={`text-sm font-bold tabular-nums ${SCORE_TONE(value)}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

function ScoreBar({ value }) {
  const tone = value >= 80 ? 'bg-emerald-500' : value >= 60 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-0.5 w-24">
      <div className={`h-full ${tone}`} style={{ width: `${Math.max(2, Math.min(100, value))}%` }} />
    </div>
  );
}
